import {
  Globe, Bot, Search, Share2
} from 'lucide-react';

const services = [
  {
    icon: Globe,
    title: 'Google Business Profile Management',
    description: 'Get found by patients who are actively searching for healthcare providers near them. We set up, optimize, and manage your Google presence for maximum visibility.',
    features: [
      'Google Business Profile setup & optimization',
      'Rank in "near me" and local map searches',
      'Add your clinic to 20+ local directories',
      'Photo & video management',
      'High-quality posts & Q&A management',
      'Manage, respond to & grow positive reviews',
    ],
    benefit: 'Patients find you first when they search for a doctor nearby.',
    span: 'lg:col-span-2',
  },
  {
    icon: Bot,
    title: 'AI-Powered Website with AI Assistant',
    description: 'A smart website with a built-in AI assistant that handles patient enquiries, provides clinic information, and books appointments — all automatically.',
    features: [
      'AI assistant trained on your clinic\'s services & specialties',
      'Handles patient enquiries 24/7 in real-time',
      'Automated appointment booking & scheduling',
      'Provides clinic info, directions & doctor availability',
      'Seamless handoff to your team for complex queries',
    ],
    benefit: 'Your AI receptionist never sleeps — every enquiry gets answered instantly.',
    span: '',
  },
  {
    icon: Search,
    title: 'SEO Services (Local + National + AI Search)',
    description: 'Dominate search results locally and nationally. We optimize your online presence so patients find you on Google, Bing, and AI search engines.',
    features: [
      'On-page & technical SEO audits',
      'Local SEO for city & region-level ranking',
      'National SEO for specialty-based searches',
      'Content strategy & medical blog writing',
      'AI search optimization (ChatGPT, Gemini, Perplexity)',
    ],
    benefit: 'Rank #1 when patients search for your specialty — locally and nationally.',
    span: '',
  },
  {
    icon: Share2,
    title: 'Social Media Marketing',
    description: 'Build trust and attract patients through strategic content on Facebook, Instagram, YouTube, and LinkedIn.',
    features: [
      'Facebook & Instagram profile setup & branding',
      '10-20 posts/month (images, carousels, infographics)',
      'Instagram Reels & YouTube Shorts for visibility',
      'Patient education tips & awareness content',
      'Community engagement & follower growth',
    ],
    benefit: 'Stay top-of-mind so patients choose you over competitors.',
    span: 'lg:col-span-2',
    featured: true,
  },
];

const Services = () => (
  <section id="services" className="bg-[#EDE7D9] py-20 lg:py-28" data-testid="services-section">
    <div className="max-w-7xl mx-auto px-6">
      <div className="text-center mb-6">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#8C6330] mb-4">What We Do</p>
        <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight text-[#11181C]">
          End-to-End Growth Solutions for Your Practice
        </h2>
      </div>
      <p className="text-base text-[#4A5568] text-center max-w-2xl mx-auto mb-14 leading-relaxed">
        From getting discovered online to managing your finances — we handle every aspect of your practice growth
        so you can focus entirely on patient care.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {services.map((service, i) => (
          <div
            key={service.title}
            className={`service-card rounded-2xl p-7 ${service.span} ${
              service.featured
                ? 'bg-[#1B3F33] border-[#1B3F33] border'
                : 'bg-white border border-[#D6CEBF] shadow-sm'
            }`}
            data-testid={`service-card-${i}`}
          >
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-5 ${
              service.featured ? 'bg-[#8C6330]/20' : 'bg-[#D4E4DA]'
            }`}>
              <service.icon size={22} className={service.featured ? 'text-[#8C6330]' : 'text-[#1B3F33]'} />
            </div>
            <h3 className={`font-heading text-lg sm:text-xl font-medium mb-2 ${
              service.featured ? 'text-white' : 'text-[#11181C]'
            }`}>
              {service.title}
            </h3>
            <p className={`text-sm leading-relaxed mb-5 ${
              service.featured ? 'text-white/70' : 'text-[#4A5568]'
            }`}>
              {service.description}
            </p>
            <ul className="space-y-2 mb-5">
              {service.features.map((f) => (
                <li key={f} className={`text-sm flex items-center gap-2 ${
                  service.featured ? 'text-white/80' : 'text-[#4A5568]'
                }`}>
                  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                    service.featured ? 'bg-[#8C6330]' : 'bg-[#265E4A]'
                  }`} />
                  {f}
                </li>
              ))}
            </ul>
            <div className={`rounded-lg px-4 py-3 ${
              service.featured ? 'bg-white/10' : 'bg-[#F9F7F2]'
            }`}>
              <p className={`text-xs font-semibold ${
                service.featured ? 'text-[#8C6330]' : 'text-[#1B3F33]'
              }`}>
                {service.benefit}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default Services;
